import { useEffect, useState, useCallback } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Megaphone, MessageCircle, Send, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function MessagesPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("announcements");
  const [announcements, setAnnouncements] = useState([]);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [unread, setUnread] = useState({}); // { [child_id]: {count, last} }

  const [annOpen, setAnnOpen] = useState(false);
  const [annForm, setAnnForm] = useState({ title: "", body: "", audience: "all_parents" });

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/unread-summary");
      setUnread(data.by_child || {});
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([
        api.get("/announcements"),
        api.get("/children"),
      ]);
      setAnnouncements(a.data);
      setChildren(c.data);
      if (c.data.length) setSelectedChild(c.data[0]);
    })();
    loadUnread();
  }, [loadUnread]);

  // Poll for unread updates every 20s
  useEffect(() => {
    const t = setInterval(loadUnread, 20000);
    return () => clearInterval(t);
  }, [loadUnread]);

  useEffect(() => {
    if (!selectedChild) return;
    (async () => {
      const { data } = await api.get(`/messages?child_id=${selectedChild.id}`);
      setMessages(data);
      // mark as read on view
      if (unread[selectedChild.id]?.count > 0) {
        try {
          await api.post(`/messages/mark-read?child_id=${selectedChild.id}`);
          loadUnread();
        } catch (e) { /* ignore */ }
      }
    })();
  }, [selectedChild, loadUnread]); // eslint-disable-line

  const publishAnn = async () => {
    try {
      await api.post("/announcements", annForm);
      toast.success("تم نشر الإعلان وإرسال إشعارات الواتساب (محاكاة)");
      setAnnOpen(false);
      setAnnForm({ title: "", body: "", audience: "all_parents" });
      const { data } = await api.get("/announcements");
      setAnnouncements(data);
    } catch (e) { toast.error(extractError(e)); }
  };

  const sendMsg = async () => {
    if (!body.trim() || !selectedChild) return;
    let recipient = null;
    if (user.role === "teacher") recipient = selectedChild.parent_id;
    if (user.role === "parent") recipient = (selectedChild.assigned_teacher_ids || [])[0];
    if (user.role === "admin") recipient = selectedChild.parent_id || (selectedChild.assigned_teacher_ids || [])[0];
    if (!recipient) { toast.error("لا يوجد مستقبل مرتبط بهذا الطفل"); return; }
    try {
      await api.post("/messages", { child_id: selectedChild.id, recipient_id: recipient, body });
      setBody("");
      const { data } = await api.get(`/messages?child_id=${selectedChild.id}`);
      setMessages(data);
    } catch (e) { toast.error(extractError(e)); }
  };

  const totalUnread = Object.values(unread).reduce((a, x) => a + (x.count || 0), 0);

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900">الرسائل والإعلانات</h1>
          <p className="text-slate-500 mt-2">بديل منظم لمجموعات الواتساب — كل محادثة موثقة</p>
        </div>
        {totalUnread > 0 && (
          <div className="inline-flex items-center gap-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-4 py-2 font-bold" data-testid="unread-total-badge">
            <Inbox className="w-4 h-4" />
            <span>{totalUnread}</span>
            <span className="text-xs">رسالة غير مقروءة</span>
          </div>
        )}
      </div>

      <div className="inline-flex bg-white rounded-xl p-1 border border-slate-200">
        <TabBtn active={tab === "announcements"} onClick={() => setTab("announcements")} testid="tab-announcements">
          <Megaphone className="w-4 h-4" /> الإعلانات
        </TabBtn>
        <TabBtn active={tab === "chats"} onClick={() => setTab("chats")} testid="tab-chats">
          <MessageCircle className="w-4 h-4" /> المحادثات الخاصة
          {totalUnread > 0 && (
            <span className="ms-1 bg-rose-600 text-white text-[10px] font-black rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
              {totalUnread}
            </span>
          )}
        </TabBtn>
      </div>

      {tab === "announcements" && (
        <div className="space-y-4">
          {user.role === "admin" && (
            <Dialog open={annOpen} onOpenChange={setAnnOpen}>
              <DialogTrigger asChild>
                <Button className="h-12 gap-2 bg-sky-600 hover:bg-sky-700" data-testid="new-announcement-btn">
                  <Megaphone className="w-4 h-4" /> إعلان جديد
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="text-2xl font-black">إعلان جديد</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-1.5">
                    <Label className="font-bold">العنوان</Label>
                    <Input data-testid="ann-title" value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-bold">المحتوى</Label>
                    <Textarea data-testid="ann-body" rows={5} value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-bold">الجمهور</Label>
                    <Select value={annForm.audience} onValueChange={(v) => setAnnForm({ ...annForm, audience: v })}>
                      <SelectTrigger data-testid="ann-audience"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_staff">كل الموظفين</SelectItem>
                        <SelectItem value="all_parents">كل أولياء الأمور</SelectItem>
                        <SelectItem value="everyone">الجميع</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="publish-ann-btn" onClick={publishAnn} className="w-full h-12 bg-sky-600 hover:bg-sky-700">نشر</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {announcements.map((a) => (
              <Card key={a.id} className="border-slate-200/70 card-elevated">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
                      <Megaphone className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-black text-slate-900">{a.title}</div>
                      <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{a.body}</div>
                      <div className="text-xs text-slate-400 mt-3">
                        {a.author_name} · {new Date(a.timestamp).toLocaleString("ar-EG")}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {announcements.length === 0 && <Card className="md:col-span-2"><CardContent className="p-16 text-center text-slate-400">لا توجد إعلانات</CardContent></Card>}
          </div>
        </div>
      )}

      {tab === "chats" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-slate-200/70 md:col-span-1">
            <CardContent className="p-4 space-y-1">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">اختر طفلاً</div>
              {children.map((c) => {
                const uc = unread[c.id]?.count || 0;
                const active = selectedChild?.id === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`chat-child-${c.id}`}
                    onClick={() => setSelectedChild(c)}
                    className={`w-full text-right p-3 rounded-lg transition-all flex items-center justify-between gap-2 ${active ? "bg-sky-600 text-white" : "hover:bg-slate-100 text-slate-800"}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className={`font-bold truncate ${uc > 0 && !active ? "text-slate-900" : ""}`}>{c.name}</div>
                      <div className={`text-xs truncate ${active ? "text-sky-100" : "text-slate-500"}`}>{c.disability_type}</div>
                    </div>
                    {uc > 0 && (
                      <span
                        data-testid={`unread-badge-${c.id}`}
                        className={`shrink-0 min-w-[22px] h-[22px] px-1.5 rounded-full flex items-center justify-center text-[11px] font-black ${active ? "bg-white text-sky-700" : "bg-rose-600 text-white"}`}
                      >
                        {uc}
                      </span>
                    )}
                  </button>
                );
              })}
              {children.length === 0 && <div className="text-sm text-slate-400 text-center py-6">لا يوجد أطفال</div>}
            </CardContent>
          </Card>
          <Card className="border-slate-200/70 md:col-span-2 flex flex-col min-h-[500px]">
            <CardContent className="p-4 flex-1 flex flex-col">
              {!selectedChild ? (
                <div className="flex-1 flex items-center justify-center text-slate-400">اختر طفلاً لعرض المحادثة</div>
              ) : (
                <>
                  <div className="border-b border-slate-200 pb-3 mb-3">
                    <div className="font-black text-slate-900">محادثة بخصوص: {selectedChild.name}</div>
                  </div>
                  <div className="flex-1 overflow-y-auto space-y-3 py-2">
                    {messages.length === 0 ? (
                      <div className="text-center text-slate-400 py-12">لا توجد رسائل بعد</div>
                    ) : (
                      messages.map((m) => (
                        <div key={m.id} className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] p-3 rounded-2xl text-sm ${m.sender_id === user.id ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                            <div className={`text-xs font-bold mb-1 flex items-center gap-1.5 ${m.sender_id === user.id ? "text-sky-100" : "text-slate-600"}`}>
                              {m.sender_name}
                              {m.source === "whatsapp_inbound" && (
                                <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-800 rounded-full px-1.5 py-0.5 text-[9px] font-black">
                                  عبر واتساب
                                </span>
                              )}
                            </div>
                            <div className="whitespace-pre-wrap">{m.body}</div>
                            <div className={`text-[10px] mt-1 ${m.sender_id === user.id ? "text-sky-200" : "text-slate-400"}`}>{new Date(m.timestamp).toLocaleString("ar-EG")}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t border-slate-200">
                    <Textarea
                      data-testid="chat-input"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="اكتب رسالتك..."
                      rows={2}
                      className="flex-1 resize-none"
                    />
                    <Button data-testid="send-msg-btn" onClick={sendMsg} className="h-12 bg-sky-600 hover:bg-sky-700 gap-2">
                      <Send className="w-4 h-4" /> إرسال
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${active ? "bg-sky-600 text-white" : "text-slate-600 hover:text-slate-900"}`}
    >
      {children}
    </button>
  );
}
